import { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Payments.css';

export default function Payments() {
  const [companies, setCompanies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [activeTab, setActiveTab] = useState('create');
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    company_id: '',
    amount: '',
    due_date: '',
    qr_code_url: '',
    description: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (form.company_id) {
      fetchPayments(form.company_id);
    }
  }, [form.company_id]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (err) {
      setError('Failed to load companies');
      setLoading(false);
    }
  };

  const fetchPayments = async (companyId) => {
    if (!companyId) return;
    try {
      const response = await api.get(`/payments/company/${companyId}`);
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };

  const handleReset = () => {
    setForm({
      company_id: form.company_id,
      amount: '',
      due_date: '',
      qr_code_url: '',
      description: '',
      status: 'pending'
    });
    setEditingId(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const companyId = parseInt(form.company_id);
      const amount = parseFloat(form.amount);

      if (!form.company_id || !form.amount || !form.due_date) {
        setError('Company, amount, and due date required');
        setSubmitting(false);
        return;
      }

      if (!companyId || isNaN(companyId)) {
        setError('Invalid company selected');
        setSubmitting(false);
        return;
      }

      if (!amount || isNaN(amount) || amount <= 0) {
        setError('Invalid amount');
        setSubmitting(false);
        return;
      }

      const payload = {
        company_id: companyId,
        amount: amount,
        due_date: form.due_date,
        qr_code_url: form.qr_code_url || null,
        description: form.description || null,
        status: form.status
      };

      if (editingId) {
        await api.patch(`/payments/${editingId}`, payload);
        setSuccess('Payment updated successfully!');
      } else {
        await api.post('/payments', payload);
        setSuccess('Invoice created successfully!');
      }

      setTimeout(() => setSuccess(''), 3000);
      handleReset();
      fetchPayments(companyId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save payment');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.patch(`/payments/${id}/paid`);
      setSuccess('Payment marked as paid!');
      setTimeout(() => setSuccess(''), 3000);
      fetchPayments(form.company_id);
    } catch (err) {
      setError('Failed to mark payment as paid');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await api.delete(`/payments/${id}`);
      setSuccess('Payment deleted!');
      setTimeout(() => setSuccess(''), 3000);
      fetchPayments(form.company_id);
    } catch (err) {
      setError('Failed to delete payment');
    }
  };

  const handleEdit = (payment) => {
    setForm({
      company_id: payment.company_id.toString(),
      amount: payment.amount,
      due_date: payment.due_date ? payment.due_date.split('T')[0] : '',
      qr_code_url: payment.qr_code_url || '',
      description: payment.description || '',
      status: payment.status
    });
    setEditingId(payment.id);
    setActiveTab('create');
  };

  const sendViaWhatsApp = async (payment) => {
    try {
      const company = companies.find(c => c.id === payment.company_id);
      if (!company) {
        setError('Company not found');
        return;
      }

      if (!company.phone) {
        setError('Company phone number not found');
        return;
      }

      const message = `Hi ${company.name},

Your invoice is ready for payment:

Invoice ID: INV-${payment.id}
Amount: ₹${parseFloat(payment.amount).toFixed(2)}
Due Date: ${new Date(payment.due_date).toLocaleDateString('en-GB')}
${payment.description ? `Details: ${payment.description}` : ''}

Please arrange payment at your earliest convenience.

Thank you!`;

      await api.post('/messages/send', {
        company_id: payment.company_id,
        phone: company.phone,
        message: message
      });

      setSuccess('Invoice sent via WhatsApp!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to send via WhatsApp: ' + (err.response?.data?.error || err.message));
      console.error('WhatsApp send error:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, paidDate) => {
    const baseClass = `status-badge status-${status}`;
    if (status === 'paid') {
      return <span className={baseClass}>✓ Paid on {formatDate(paidDate)}</span>;
    }
    return <span className={baseClass}>{status === 'pending' ? '⏳ Pending' : '⚠️ ' + status}</span>;
  };

  if (loading) return <div className="payments-container"><p>Loading...</p></div>;

  return (
    <div className="payments-container">
      <h1>💳 Payments & Invoices</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="payment-selector">
        <label>Select Company:</label>
        <select
          value={form.company_id}
          onChange={(e) => setForm({ ...form, company_id: e.target.value })}
          className="form-select"
        >
          <option value="">-- Choose a company --</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
          ))}
        </select>
      </div>

      {form.company_id && (
        <div className="payment-tabs">
          <button
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            {editingId ? '✏️ Edit Invoice' : '➕ Create Invoice'}
          </button>
          <button
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => { setActiveTab('history'); setEditingId(null); }}
          >
            📋 Payment History
          </button>
          <button
            className={`tab-btn ${activeTab === 'automation' ? 'active' : ''}`}
            onClick={() => setActiveTab('automation')}
          >
            ⚙️ Automation
          </button>
        </div>
      )}

      {activeTab === 'create' && form.company_id && (
        <form onSubmit={handleSubmit} className="payment-form">
          <h2>{editingId ? 'Edit Invoice' : 'Create New Invoice'}</h2>

          <div className="form-group">
            <label>Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="5000.00"
              required
            />
          </div>

          <div className="form-group">
            <label>Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>QR Code URL (Payment)</label>
            <input
              type="url"
              value={form.qr_code_url}
              onChange={(e) => setForm({ ...form, qr_code_url: e.target.value })}
              placeholder="https://example.com/qr/..."
            />
            {form.qr_code_url && (
              <div className="qr-preview">
                <img src={form.qr_code_url} alt="QR Code" width="150" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Description/Notes</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Invoice details, reference number, etc."
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="form-select"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : editingId ? 'Update Invoice' : 'Create Invoice'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      )}

      {activeTab === 'history' && form.company_id && (
        <div className="payments-list">
          <h2>Payment History</h2>
          {payments.length === 0 ? (
            <p className="empty-state">No payments yet. Create one to get started.</p>
          ) : (
            <div className="payments-grid">
              {payments.map(payment => (
                <div key={payment.id} className="payment-card">
                  <div className="payment-header">
                    <h3>₹{parseFloat(payment.amount).toFixed(2)}</h3>
                    {getStatusBadge(payment.status, payment.paid_date)}
                  </div>

                  <div className="payment-details">
                    <p><strong>Due:</strong> {formatDate(payment.due_date)}</p>
                    {payment.description && (
                      <p><strong>Details:</strong> {payment.description}</p>
                    )}
                    {payment.qr_code_url && (
                      <div className="qr-thumbnail">
                        <img src={payment.qr_code_url} alt="QR" width="100" />
                      </div>
                    )}
                  </div>

                  <div className="payment-actions">
                    {payment.status !== 'paid' && (
                      <>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleMarkPaid(payment.id)}
                        >
                          ✓ Mark Paid
                        </button>
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => sendViaWhatsApp(payment)}
                        >
                          💬 Send WhatsApp
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleEdit(payment)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(payment.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'automation' && (
        <div className="automation-info">
          <h2>⚙️ Automation Settings</h2>
          <div className="info-box">
            <h3>📧 Daily Reminder System</h3>
            <p><strong>Schedule:</strong> Every day at 8:00 AM IST (2:30 AM UTC)</p>
            <p><strong>Task:</strong> Automatically send payment reminders for overdue invoices</p>
            <p><strong>Status:</strong> ✅ Active (cronjob running)</p>

            <h3>🔄 Automation Flow</h3>
            <ul>
              <li>1. System checks all payments with status "pending"</li>
              <li>2. Filters those with due_date in past</li>
              <li>3. Marks them as "overdue"</li>
              <li>4. Sends WhatsApp reminder with invoice details</li>
              <li>5. Logs activity to audit trail</li>
            </ul>

            <h3>⚙️ Configuration</h3>
            <p><strong>Cron Job:</strong> <code>0 2 30 * * *</code> (UTC)</p>
            <p><strong>File:</strong> <code>/var/www/nasar.thepremium.in/backend/jobs/dailyReminder.js</code></p>
            <p><strong>Service:</strong> Managed by systemd (nasar-backend)</p>

            <h3>💡 Manual Actions</h3>
            <p>Use the "Payment History" tab to:</p>
            <ul>
              <li>Manually mark payments as paid</li>
              <li>Send instant WhatsApp reminders</li>
              <li>Update invoice details</li>
              <li>Delete incorrect records</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
